import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { useNavigate } from '@tanstack/react-router';
import CustomerAdminService from './customerAdmin.service';
import AttachmentService from '../attachment/attachment.service';

type ActionType = 'createCustomer' | 'updateCustomer' | 'createAttachmentType' | 'updateAttachmentType' | 'deleteAttachmentType';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useCustomerMutation = (): {
  createCustomer: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  updateCustomer: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  createAttachmentType: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  updateAttachmentType: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  deleteAttachmentType: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  createCustomerMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  updateCustomerMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  createAttachmentTypeMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  updateAttachmentTypeMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  deleteAttachmentTypeMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  isUpdatingCreateCustomer: Record<string, boolean>;
  setIsUpdatingCreateCustomer: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingUpdateCustomer: Record<string, boolean>;
  setIsUpdatingUpdateCustomer: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingCreateAttachmentType: Record<string, boolean>;
  setIsUpdatingCreateAttachmentType: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingUpdateAttachmentType: Record<string, boolean>;
  setIsUpdatingUpdateAttachmentType: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isUpdatingDeleteAttachmentType: Record<string, boolean>;
  setIsUpdatingDeleteAttachmentType: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingCreateCustomer, setIsUpdatingCreateCustomer] = useState<Record<string, boolean>>({});
  const [isUpdatingUpdateCustomer, setIsUpdatingUpdateCustomer] = useState<Record<string, boolean>>({});
  const [isUpdatingCreateAttachmentType, setIsUpdatingCreateAttachmentType] = useState<Record<string, boolean>>({});
  const [isUpdatingUpdateAttachmentType, setIsUpdatingUpdateAttachmentType] = useState<Record<string, boolean>>({});
  const [isUpdatingDeleteAttachmentType, setIsUpdatingDeleteAttachmentType] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const customerAdminService = CustomerAdminService();
    const attachmentService = AttachmentService();

    const action: Record<ActionType, (payload: any) => Promise<any>> = {
      createCustomer: (params) => customerAdminService.createCustomerAdmin(params.payload),
      updateCustomer: (params) => customerAdminService.updateCustomerAdmin(params.payload),
      createAttachmentType: (params) => attachmentService.createAttachmentType(params.payload),
      updateAttachmentType: (params) => attachmentService.updateAttachmentType(params.payload),
      deleteAttachmentType: (params) => attachmentService.deleteAttachmentType(params.payload),
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, ...newAttachment }) => {
      setIsUpdatingCreateCustomer({ ...isUpdatingCreateCustomer, [actionType]: true });
      setIsUpdatingUpdateCustomer({ ...isUpdatingUpdateCustomer, [actionType]: true });
      setIsUpdatingCreateAttachmentType({ ...isUpdatingCreateAttachmentType, [actionType]: true });
      setIsUpdatingUpdateAttachmentType({ ...isUpdatingUpdateAttachmentType, [actionType]: true });
      setIsUpdatingDeleteAttachmentType({ ...isUpdatingDeleteAttachmentType, [actionType]: true });
    },
    onSuccess: (data: any, variables: any) => {
      console.log('Customer Mutation data:', data);
      if (variables.actionType !== 'deleteAttachmentType') {
        navigate({ to: `/customers` });
      }
      if (variables.actionType === 'createCustomer') {
        enqueueSnackbar('Customer created successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Customer updated successfully', { variant: 'success' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      if (variables.actionType === 'updateCustomer') {
        enqueueSnackbar('Error updating customer', { variant: 'error' });
      } else {
        enqueueSnackbar('Error creating customer', { variant: 'error' });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['customerAdmin'] });
      queryClient.invalidateQueries({ queryKey: ['attachment-types-by-customer'] });
      queryClient.invalidateQueries({ queryKey: ['attachment-types-count'] });
    },
  });

  return {
    createCustomer: mutation.mutate,
    createCustomerMutation: mutation,
    isUpdatingCreateCustomer,
    setIsUpdatingCreateCustomer,
    updateCustomer: mutation.mutate,
    updateCustomerMutation: mutation,
    isUpdatingUpdateCustomer,
    setIsUpdatingUpdateCustomer,
    createAttachmentType: mutation.mutate,
    createAttachmentTypeMutation: mutation,
    isUpdatingCreateAttachmentType,
    setIsUpdatingCreateAttachmentType,
    updateAttachmentType: mutation.mutate,
    updateAttachmentTypeMutation: mutation,
    isUpdatingUpdateAttachmentType,
    setIsUpdatingUpdateAttachmentType,
    deleteAttachmentType: mutation.mutate,
    deleteAttachmentTypeMutation: mutation,
    isUpdatingDeleteAttachmentType,
    setIsUpdatingDeleteAttachmentType
  };
};

export default useCustomerMutation;
